import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Printer, ShoppingCart, Star, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { printing3DProducts, contactInfo } from '../data/mock';
import PurchaseModal from './PurchaseModal';

const Printing3DSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const categories = ['Todos', ...new Set(printing3DProducts.map(p => p.category))];
  
  const filteredProducts = selectedCategory === 'Todos' 
    ? printing3DProducts 
    : printing3DProducts.filter(p => p.category === selectedCategory);

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleBuyProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleConsultar = (product) => {
    const message = `Hola! Me interesa consultar sobre: ${product.name}. ¿Podrían darme más información?`;
    const whatsappUrl = `https://wa.me/${contactInfo.social.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  return (
    <section className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600/20 border border-orange-500/30 rounded-full mb-6"
          >
            <Printer className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-mono tracking-wider uppercase">Impresión 3D</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Impresión 3D
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Damos vida a tus ideas. Servicios de impresión 3D, diseño y venta de insumos.
          </motion.p>
        </div>

        {/* Category Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-10"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-mono tracking-wide transition-all
                ${selectedCategory === category 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-orange-900/30'
                }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Products Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredProducts.map((product) => (
            <motion.div 
              key={product.id} 
              variants={itemVariants}
              whileHover={{ y: -4 }}
              className="cursor-pointer"
            >
              <Card className={`bg-white/5 border-orange-900/30 hover:border-orange-500/50 transition-all h-full flex flex-col relative overflow-hidden hover:shadow-lg hover:shadow-orange-500/10
                ${product.featured ? 'ring-1 ring-orange-500/50' : ''}`}
              >
                {/* Product Image */}
                <div className="relative h-48 overflow-hidden bg-black/50">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-orange-600/90 text-white rounded-full text-xs font-mono backdrop-blur-sm">
                      {product.category}
                    </span>
                  </div>
                  {product.featured && (
                    <div className="absolute top-3 right-3">
                      <span className="flex items-center gap-1 px-2 py-1 bg-orange-600 text-white rounded-full text-xs font-mono">
                        <Star className="w-3 h-3 fill-current" />
                        Destacado
                      </span>
                    </div>
                  )}
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">{product.name}</CardTitle>
                  <CardDescription className="text-gray-500">
                    {product.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <div className="flex items-center justify-between">
                    {product.price ? (
                      <span className="text-2xl font-bold text-orange-400">
                        {formatPrice(product.price)}
                      </span>
                    ) : (
                      <span className="text-xl font-bold text-orange-400">
                        {product.priceText || 'Consultar'}
                      </span>
                    )}
                  </div>
                </CardContent>
                <CardFooter>
                  {product.price ? (
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyProduct(product);
                      }}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full font-mono text-xs tracking-wider uppercase flex items-center justify-center gap-2"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Comprar
                    </Button>
                  ) : (
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConsultar(product);
                      }}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full font-mono text-xs tracking-wider uppercase flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Consultar
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-orange-900/20 via-orange-600/20 to-orange-900/20 border border-orange-500/30 rounded-2xl p-8 max-w-3xl mx-auto">
            <Printer className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">¿Tenés un proyecto en mente?</h3>
            <p className="text-gray-400 mb-6">
              Contanos tu idea y te ayudamos a hacerla realidad. Cotizamos sin compromiso.
            </p>
            <Button 
              onClick={() => {
                const message = "Hola! Quiero consultar sobre un proyecto de impresión 3D. ¿Podrían ayudarme?";
                const whatsappUrl = `https://wa.me/${contactInfo.social.whatsapp}?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-3 font-semibold text-base inline-flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Consultá tu proyecto
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Purchase Modal */}
      <PurchaseModal 
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default Printing3DSection;
